import { fetchIsPostalCodeValid } from '@/entities/facility';
import {
  checkToFormatPhoneNumber,
  handleFetchDivisions,
  showMessageError,
  checkNull,
} from '@/shared/lib';
import { useConstructor, useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import type { optionSelect } from '@/shared/ui';
import _ from 'lodash';
import { useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { t } from 'ttag';
import type {
  AutoCompleteInputData,
  NewStudyStep2ErrorObject,
  NewStudyStep2Props,
  NewStudyStep2State,
} from './newStudyStep2.types';

/**
 * Drives the patient contact step of the new-study wizard, holding address, phone, email and secondary contact data plus country, division and error state. Seeds itself through createInitialData on every defaultData change, derives whether email is mandatory for Biodirect or non-US Biotres studies and whether US or CA phone formatting applies, exposes onClickBottomButtons as handleNavigation on ref, and scrolls to the email field when it is required but empty.
 *
 * @param defaultData - Previously captured contact details used to prefill the form.
 * @param countries - Country options the initial country is resolved against.
 * @param handleClick - Wizard callback receiving the step2 payload and the direction flag.
 * @param isUsingBiofluxDirect - True when the study ships as Biodirect, which makes the patient email mandatory.
 * @param isUsingBiotresDevice - True when a Biotres device is used, making email mandatory outside the US.
 * @param setIsLoadedData - Callback flagging whether the step has finished seeding its data.
 * @param setIsLoading - Callback toggling the page spinner around submit validation.
 * @param setIsLoadingDivisions - Callback toggling the spinner of the state and province select.
 * @param isDeviceAssignmentPending - True when device assignment is still pending, which lifts the email requirement.
 * @param ref - Forwarded ref exposing handleNavigation so the wizard can trigger validation.
 * @returns The step 2 state, the email-required and phone-format flags, and the change and autocomplete handlers.
 */
const useNewStudyStep2 = (
  {
    defaultData,
    countries,
    handleClick,
    isUsingBiofluxDirect,
    isUsingBiotresDevice,
    setIsLoadedData,
    setIsLoading,
    setIsLoadingDivisions,
    isDeviceAssignmentPending,
  }: NewStudyStep2Props,
  ref: any,
) => {
  const isInitial = useRef(false);

  const [state, setState] = useMergeState<NewStudyStep2State>({
    errorObject: {},
    newStudyStep2Data: {
      country: undefined,
      address: undefined,
      city: undefined,
      state: undefined,
      zip: undefined,
      phone1: undefined,
      email: undefined,
      phone2: undefined,
      secondaryName: undefined,
      secondaryRelationship: undefined,
      secondaryPhone: undefined,
      secondaryEmail: undefined,
      ...defaultData,
    },
    country: undefined,
    state: undefined,
  });

  /**
   * Whether the patient's email address is mandatory for the study being created. True when device assignment is not pending and either Biodirect is used or a Biotres device is used with a country other than US.
   *
   * @returns True when the email field must be filled in.
   */
  const isEmailRequired =
    !isDeviceAssignmentPending &&
    (isUsingBiofluxDirect || (isUsingBiotresDevice && state.newStudyStep2Data.country !== 'US'));

  /**
   * Seeds the step 2 patient contact form from defaultData and the country list of the new-study wizard. Resolves the country by value or label match and falls back to US, fetches divisions for US and CA while raising the divisions loading flag, preselects the division matching defaultData.state, then commits country, divisions and merged form data and marks the step loaded.
   *
   * @returns A promise settling once the seeded state has been committed and the loaded flag raised.
   */
  const createInitialData = async () => {
    isInitial.current = true;

    const updatedNewStudyStep2Data = { ...state.newStudyStep2Data };
    _.merge(updatedNewStudyStep2Data, defaultData);

    let defaultCountry;
    if (defaultData.country) {
      defaultCountry = _.find(
        countries,
        (x) => x.value === defaultData.country || _.includes(x.label, defaultData.country),
      );
    } else {
      defaultCountry = _.find(countries, (x) => x.value === 'US'); // *: Default country is United States
    }

    if (defaultCountry) {
      const setStateObject = { country: defaultCountry };

      const countryCode = defaultCountry.value;

      if (countryCode === 'US' || countryCode === 'CA') {
        setIsLoadingDivisions(true);

        const divisions = await handleFetchDivisions(countryCode);
        _.assign(setStateObject, { divisions });

        if (defaultData.state) {
          /**
           * Division option from the fetched divisions list that matches the state carried in defaultData. Matched either by exact value equality or by the state string being contained in the option label.
           *
           * @returns The matching division option, or undefined when none matches.
           */
          const defaultState = _.find(
            divisions,
            (x) => x.value === defaultData.state || _.includes(x.label, defaultData.state),
          );
          if (defaultState) {
            _.assign(setStateObject, { state: defaultState });
          }
        }
      }

      updatedNewStudyStep2Data.country = countryCode;

      _.assign(setStateObject, {
        newStudyStep2Data: updatedNewStudyStep2Data,
      });

      setState(setStateObject);
      setIsLoadedData(true);
      setIsLoadingDivisions(false);
    } else {
      setIsLoadedData(true);
    }
  };

  /**
   * Writes one patient contact value into the step 2 data of the new-study wizard. Copies newStudyStep2Data, assigns the value under the given field name and stores the copy.
   *
   * @param name - Key inside newStudyStep2Data to write.
   * @param value - Value entered for that field.
   * @returns Nothing.
   */
  const onChange = (name: string, value: string) => {
    const updatedNewStudyStep2Data = { ...state.newStudyStep2Data };
    (updatedNewStudyStep2Data as any)[name] = value;
    setState({ newStudyStep2Data: updatedNewStudyStep2Data });
  };

  /**
   * Handles a dropdown choice in the step 2 patient contact form. A country change refetches its divisions when checkToFormatPhoneNumber accepts the code and blanks address, city, state, zip and all three phone fields plus the selected division; the picked option and the updated newStudyStep2Data are then committed and the divisions loading flag lowered.
   *
   * @param name - Name of the select whose value changed, treated specially for 'country'.
   * @param value - Code of the chosen option written into the form data.
   * @param option - Whole chosen option kept in state under the select name for display.
   * @returns A promise settling once the selection and any refetched divisions are in state.
   */
  const onChangeSelection = async (name: string, value: string, option: optionSelect) => {
    const setStateObject = { [name]: option };
    const updatedNewStudyStep2Data = { ...state.newStudyStep2Data };

    if (name === 'country') {
      if (checkToFormatPhoneNumber(value)) {
        setIsLoadingDivisions(true);
        const divisions = await handleFetchDivisions(value);
        _.assign(setStateObject, { divisions });
      }

      updatedNewStudyStep2Data.address = '';
      updatedNewStudyStep2Data.city = '';
      updatedNewStudyStep2Data.state = '';
      updatedNewStudyStep2Data.zip = '';
      updatedNewStudyStep2Data.phone1 = '';
      updatedNewStudyStep2Data.phone2 = '';
      updatedNewStudyStep2Data.secondaryPhone = '';
      _.assign(setStateObject, { state: undefined });
    }

    (updatedNewStudyStep2Data as any)[name] = value;
    _.assign(setStateObject, { newStudyStep2Data: updatedNewStudyStep2Data });

    setState(setStateObject);
    setIsLoadingDivisions(false);
  };

  /**
   * Wipes the resolved address fields of the step 2 form when an autocomplete input is cleared. Blanks city, state and zip, also blanks address unless the cleared input was the city search, and drops the selected division option.
   *
   * @param isSearchCity - True when the cleared input searched cities, which keeps the address text intact.
   * @returns Nothing.
   */
  const clearAutoComplete = (isSearchCity: boolean | undefined) => {
    const updatedNewStudyStep2Data = { ...state.newStudyStep2Data };

    updatedNewStudyStep2Data.city = '';
    updatedNewStudyStep2Data.state = '';
    updatedNewStudyStep2Data.zip = '';
    if (!isSearchCity) {
      updatedNewStudyStep2Data.address = '';
    }

    setState({
      state: undefined,
      newStudyStep2Data: updatedNewStudyStep2Data,
    });
  };

  /**
   * Keeps the raw text typed into the step 2 address or city autocomplete inside the form data. Assigns the text to city when the city search is active and to address otherwise.
   *
   * @param value - Current text of the autocomplete input.
   * @param isSearchCity - True when the text belongs to the city search rather than the address one.
   * @returns Nothing.
   */
  const onChangeInputAutoComplete = (value: string, isSearchCity: boolean | undefined) => {
    const updatedNewStudyStep2Data = { ...state.newStudyStep2Data };
    if (!isSearchCity) {
      updatedNewStudyStep2Data.address = value;
    } else {
      updatedNewStudyStep2Data.city = value;
    }
    setState({ newStudyStep2Data: updatedNewStudyStep2Data });
  };

  /**
   * Applies a place chosen from the step 2 address or city autocomplete to the patient contact data. Copies address, city, the state code and zip off the suggestion, refetches divisions when the suggested country differs from the current one, keeps a state option only for US and CA and clears it otherwise, then commits the merged state and lowers the divisions loading flag.
   *
   * @param data - Address components of the chosen suggestion, including nested country and state objects.
   * @param isSearchCity - True when the suggestion came from the city search, leaving the address text untouched.
   * @returns A promise settling once the resolved address has been merged into state.
   */
  const onChangeAutoComplete = async (
    data: AutoCompleteInputData,
    isSearchCity: boolean | undefined,
  ) => {
    const {
      country: autoCompleteCountry,
      address: autoCompleteAddress,
      city: autoCompleteCity,
      state: autoCompleteState,
      zip: autoCompleteZip,
    } = data;

    const setStateObject = {};

    const updatedNewStudyStep2Data = { ...state.newStudyStep2Data };
    if (!isSearchCity) {
      updatedNewStudyStep2Data.address = autoCompleteAddress;
    }
    updatedNewStudyStep2Data.city = autoCompleteCity;
    updatedNewStudyStep2Data.state = autoCompleteState?.code;
    updatedNewStudyStep2Data.zip = autoCompleteZip;

    if (autoCompleteCountry) {
      const autoCompleteCountryCode = autoCompleteCountry?.code;

      if (autoCompleteCountryCode) {
        if (autoCompleteCountryCode !== updatedNewStudyStep2Data.country) {
          updatedNewStudyStep2Data.country = autoCompleteCountryCode;
          setIsLoadingDivisions(true);
          const divisions = await handleFetchDivisions(autoCompleteCountryCode);
          _.assign(setStateObject, { divisions });
        }

        if (autoCompleteCountryCode === 'US' || autoCompleteCountryCode === 'CA') {
          _.assign(setStateObject, {
            state: {
              label: autoCompleteState.name,
              value: autoCompleteState.code,
            },
          });
        } else {
          _.assign(setStateObject, { state: undefined });
        }
      }
    }

    _.assign(setStateObject, { newStudyStep2Data: updatedNewStudyStep2Data });

    setState(setStateObject);
    setIsLoadingDivisions(false);
  };

  /**
   * Validates the step 2 patient contact data before the wizard advances, and forwards it untouched when going back. Advancing runs checkNull over country, address, city, state, zip, phones and emails, verifies US and CA zip codes through fetchIsPostalCodeValid using the matched division fullCode with a Canadian zip trimmed at the first space, and blocks with an error toast plus field messages on any failure or on an email missing for Biodirect or Biocore and Biotres.
   *
   * @param isNextClicked - True when advancing, which triggers validation, false when stepping back.
   * @returns A promise settling once validation finished and handleClick has run or errors were stored.
   */
  const onClickBottomButtons = async (isNextClicked: boolean) => {
    if (isNextClicked) {
      setIsLoading(true);

      const errorObject: NewStudyStep2ErrorObject = {};
      const checkNullData = [
        { field: 'country', value: state.newStudyStep2Data.country, isRequired: true },
        { field: 'address', value: state.newStudyStep2Data.address, isRequired: true },
        { field: 'city', value: state.newStudyStep2Data.city, isRequired: true },
        { field: 'state', value: state.newStudyStep2Data.state, isRequired: true },
        { field: 'zip', value: state.newStudyStep2Data.zip, isRequired: true },
        { field: 'phone1', value: state.newStudyStep2Data.phone1, isRequired: true },
        { field: 'email', value: state.newStudyStep2Data.email, isRequired: false },
        { field: 'phone2', value: state.newStudyStep2Data.phone2, isRequired: false },
        {
          field: 'secondaryPhone',
          value: state.newStudyStep2Data.secondaryPhone,
          isRequired: false,
        },
        {
          field: 'secondaryEmail',
          value: state.newStudyStep2Data.secondaryEmail,
          isRequired: false,
        },
      ];
      const checkNullError = checkNull(checkNullData);
      if (!_.isEmpty(checkNullError)) {
        _.assign(errorObject, { ...checkNullError });
      }

      if (!errorObject.zip) {
        // *: Continue check zip code validity if it has value
        try {
          if (
            state.newStudyStep2Data.country === 'US' ||
            state.newStudyStep2Data.country === 'CA'
          ) {
            /**
             * Postal code value normalized before being sent for validation from the step 2 form. Keeps only the segment before the first space for Canadian addresses and the raw zip value for every other country.
             *
             * @returns The normalized postal code.
             */
            const nzipCode =
              state.newStudyStep2Data.country === 'CA' && state.newStudyStep2Data.zip
                ? state.newStudyStep2Data.zip.split(' ')[0]
                : state.newStudyStep2Data.zip;
            /**
             * Division entry from the loaded divisions list corresponding to the state currently entered in the step 2 form. Matched by exact value equality or by the state string appearing in the option label, and consumed for its fullCode when validating the postal code.
             *
             * @returns The matching division entry, or undefined when none matches.
             */
            const division = _.find(
              state.divisions,
              (x) =>
                x.value === state.newStudyStep2Data.state ||
                _.includes(x.label, state.newStudyStep2Data.state),
            );
            const isPostalCodeValid = await fetchIsPostalCodeValid({
              division: division?.fullCode as string,
              postalCode: nzipCode as string,
            });
            if (!isPostalCodeValid) {
              showMessageError({ content: t`Invalid zip code` });
              _.assign(errorObject, { zip: 'Invalid value' });
            }
          }
        } catch (error) {
          console.error('Failed to check zip code: ', error);
          showMessageError({ content: t`Invalid zip code` });
          _.assign(errorObject, { zip: 'Invalid value' });
        }
      }

      if (!_.isEmpty(errorObject)) {
        showMessageError({
          content: t`Some information is invalid. Please review the highlighted field(s).`,
        });
        setState({ errorObject });
        setIsLoading(false);
        return;
      }

      if (isEmailRequired && !state.newStudyStep2Data.email) {
        showMessageError({
          content: t`Some information is invalid. Please review the highlighted field(s).`,
        });
        setState({
          errorObject: {
            email: isUsingBiofluxDirect
              ? "Biodirect requires the patient's email for shipping purposes"
              : "Biocore requires the patient's email for notification purposes.",
          },
        });
        setIsLoading(false);
        return;
      }

      handleClick({ step2: state.newStudyStep2Data }, true);
      setIsLoading(false);
    } else {
      handleClick({ step2: state.newStudyStep2Data }, false);
      setIsLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    handleNavigation: onClickBottomButtons,
  }));

  useConstructor(() => {
    setIsLoadedData(false);
  });

  useEffect(() => {
    createInitialData();
  }, [defaultData]);

  // *: Clear errorObject when changing input fields
  useUpdateEffect(() => {
    if (isInitial.current) {
      isInitial.current = false;
      if (isEmailRequired && !state.newStudyStep2Data.email) {
        const emailElement = document.getElementById('email');
        if (emailElement) {
          emailElement.scrollIntoView();
        }
        setState({
          errorObject: {
            email: isUsingBiofluxDirect
              ? "Biodirect requires the patient's email for shipping purposes"
              : "Biocore requires the patient's email for notification purposes.",
          },
        });
      }
    } else if (!_.isEmpty(state.errorObject)) {
      setState({ errorObject: {} });
    }
  }, [state.newStudyStep2Data]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const bodyElement = document.querySelector('.app-body');
    if (bodyElement) {
      bodyElement.scroll(0, 0);
    }
  }, []);

  /**
   * Whether phone numbers and the state field of the step 2 form should use US and Canada formatting. Memoized result of checkToFormatPhoneNumber over the country stored in newStudyStep2Data.
   *
   * @returns True when the selected country uses formatted phone numbers.
   */
  const shouldFormatPhoneNumber = useMemo(
    () => checkToFormatPhoneNumber(state.newStudyStep2Data.country),
    [state.newStudyStep2Data.country],
  );

  return {
    state,
    isEmailRequired,
    shouldFormatPhoneNumber,
    onChange,
    onChangeSelection,
    clearAutoComplete,
    onChangeInputAutoComplete,
    onChangeAutoComplete,
  };
};

export { useNewStudyStep2 };
